import { ref, watch, type Ref, type WatchSource } from "vue";
import type { NotificationBannerItem } from "./index";

/**
 * The notification banners (RDT-339) for the current interface language, re-fetched whenever it
 * changes. Only the answer for the language that is current when it arrives is applied.
 *
 * The apps used to assign every answer as it came in, so with two requests in flight the slower one
 * won. The questionnaire starts in "en" and switches to the participant's language right away; when
 * the "en" answer arrived after the "de" one, a German page kept an English banner for good (RDT-437).
 *
 * A banner is decoration: a failed fetch leaves the app without one rather than breaking it.
 */
export function useNotificationBanners(
    language: WatchSource<string>,
    fetchBanners: (language: string) => Promise<NotificationBannerItem[]>,
): Ref<NotificationBannerItem[]> {
    const banners = ref<NotificationBannerItem[]>([]);

    watch(
        language,
        async (lang, _previous, onCleanup) => {
            let superseded = false;
            onCleanup(() => {
                superseded = true;
            });

            let result: NotificationBannerItem[];
            try {
                result = await fetchBanners(lang);
            } catch (error) {
                console.error("Failed to fetch notification banners:", error);
                result = [];
            }
            if (!superseded) {
                banners.value = result;
            }
        },
        { immediate: true },
    );

    return banners;
}
